import { FC, Dispatch } from "react";
import { StandardHandler } from "../../../middleware/handlers/standard-handler";
import type {
  ViewerNavigation,
  ViewerProjection,
} from "../../../core/standard/viewer-tools";

interface ViewerToolbarProps {
  projection: ViewerProjection;
  navigation: ViewerNavigation;
  setProjection: Dispatch<ViewerProjection>;
  setNavigation: Dispatch<ViewerNavigation>;
  dispatch: Dispatch<any>;
}

interface ViewerToolbarButtonProps {
  imageSource: string;
  title: string;
  active?: boolean;
  onClick: () => void;
}

export const ViewerToolbarButton: FC<ViewerToolbarButtonProps> = ({
  imageSource,
  title,
  active = false,
  onClick,
}) => {
  return (
    <button
      type="button"
      className={
        active
          ? "viewerToolbarButton viewerToolbarButtonActive"
          : "viewerToolbarButton"
      }
      title={title}
      onClick={onClick}
    >
      <img src={imageSource} draggable="false" alt={title} />
    </button>
  );
};

// Floats over the bottom of the model: how you look at it on the left, what
// you show of it on the right.
export const ViewerToolbar: FC<ViewerToolbarProps> = ({
  projection,
  navigation,
  setProjection,
  setNavigation,
  dispatch,
}) => {
  return (
    <div id="viewerToolbar">
      <ViewerToolbarButton
        imageSource="assets/icons/fit-model.png"
        title="Fit to model"
        onClick={() => {
          StandardHandler.FitModel();
        }}
      />
      <ViewerToolbarButton
        imageSource={
          projection === "Perspective"
            ? "assets/icons/perspective.png"
            : "assets/icons/orthographic.png"
        }
        title={
          projection === "Perspective"
            ? "Perspective (switch to orthographic)"
            : "Orthographic (switch to perspective)"
        }
        onClick={() => {
          const nextProjection: ViewerProjection =
            projection === "Perspective" ? "Orthographic" : "Perspective";
          StandardHandler.SetProjection(nextProjection);
          setProjection(nextProjection);
          // orthographic ends a walk
          if (nextProjection === "Orthographic") {
            setNavigation("Orbit");
          }
        }}
      />
      <ViewerToolbarButton
        imageSource="assets/icons/walk.png"
        title={
          navigation === "FirstPerson"
            ? "Walking (click to orbit)"
            : "Walk through the model"
        }
        active={navigation === "FirstPerson"}
        onClick={() => {
          const nextNavigation: ViewerNavigation =
            navigation === "FirstPerson" ? "Orbit" : "FirstPerson";
          StandardHandler.SetNavigation(nextNavigation);
          setNavigation(nextNavigation);
          // walking needs perspective
          if (nextNavigation === "FirstPerson") {
            setProjection("Perspective");
          }
        }}
      />
      <span className="viewerToolbarDivider" />
      <ViewerToolbarButton
        imageSource="assets/icons/isolate.png"
        title="Isolate selection"
        onClick={() => {
          dispatch({ type: "ISOLATE_MODEL_ELEMENT" });
        }}
      />
      <ViewerToolbarButton
        imageSource="assets/icons/hide.png"
        title="Hide selection"
        onClick={() => {
          dispatch({ type: "HIDE_MODEL_ELEMENT" });
        }}
      />
      <ViewerToolbarButton
        imageSource="assets/icons/unhide.png"
        title="Show everything"
        onClick={() => {
          dispatch({ type: "UNHIDE_ALL_MODEL_ELEMENTS" });
        }}
      />
    </div>
  );
};

export default ViewerToolbar;
